import type { QueryClient } from '@tanstack/react-query';
import type {
  ConversationMutationCacheState,
  ConversationMutationSnapshot,
} from '../../../types/cache/funnel-conversations/optimistic.types';
import {
  isInfiniteQueryData,
  isRegularQueryData,
  type InfiniteQueryData,
  type RegularQueryData,
} from 'src/modules/shared/utils/cache/update.util';

const getSnapshots = (
  queryClient: QueryClient,
  conversationId: number
): Set<ConversationMutationSnapshot> => {
  const queries = queryClient.getQueriesData<
    | InfiniteQueryData<ConversationMutationSnapshot>
    | RegularQueryData<ConversationMutationSnapshot>
    | ConversationMutationSnapshot
  >({
    predicate: ({ queryKey }) =>
      queryKey[0] === 'crm/funnel-conversations' ||
      queryKey[0] === 'crm/documents/funnel-conversations',
  });
  return new Set(
    queries.flatMap(([, data]) => {
      if (!data) return [];
      const items = isInfiniteQueryData<ConversationMutationSnapshot>(data)
        ? data.pages.flatMap((page) => page.items)
        : isRegularQueryData<ConversationMutationSnapshot>(data)
          ? data.data
          : [data];
      return items.filter((item) => String(item.id) === String(conversationId));
    })
  );
};

/**
 * Captures this save's optimistic cache ownership without trusting client clocks.
 * Structural sharing preserves unchanged item references when other rows update.
 * @param queryClient - Account-owned cache containing detail and board queries.
 * @param conversationId - CRM conversation being saved.
 * @returns A tenant-aware check for unchanged, superseded or detached snapshots.
 */
export const captureConversationMutationCache = (
  queryClient: QueryClient,
  conversationId: number
): ((tenantId: string) => ConversationMutationCacheState) => {
  const snapshots = getSnapshots(queryClient, conversationId);
  return (tenantId) => {
    const current = getSnapshots(queryClient, conversationId);
    if (
      (snapshots.size > 0 && current.size === 0) ||
      [...current].some((item) => item.tenantId !== tenantId)
    ) {
      return 'detached';
    }
    return current.size === snapshots.size &&
      [...current].every((item) => snapshots.has(item))
      ? 'current'
      : 'changed';
  };
};
